import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { format, isSameDay, parseISO } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Calendar as CalendarIcon, User, Car, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BookingCalendar = ({ bookings, onDateSelect }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookingsByDate, setBookingsByDate] = useState({});

  useEffect(() => {
    // Group bookings by date
    const grouped = {};
    bookings.forEach(booking => {
      const dateKey = booking.preferred_date || booking.date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(booking);
    });
    setBookingsByDate(grouped);
  }, [bookings]);

  const getTileContent = ({ date, view }) => {
    if (view !== 'month') return null;

    const dateStr = format(date, 'yyyy-MM-dd');
    const dayBookings = bookingsByDate[dateStr] || [];

    if (dayBookings.length === 0) return null;

    const statusCounts = {
      pending: 0,
      confirmed: 0,
      'in-progress': 0,
      completed: 0,
      cancelled: 0
    };

    dayBookings.forEach(booking => {
      statusCounts[booking.status]++;
    });

    return (
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-0.5 pb-1">
        {statusCounts.pending > 0 && (
          <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" title={`${statusCounts.pending} pending`}></div>
        )}
        {statusCounts.confirmed > 0 && (
          <div className="w-1.5 h-1.5 rounded-full bg-blue-500" title={`${statusCounts.confirmed} confirmed`}></div>
        )}
        {statusCounts['in-progress'] > 0 && (
          <div className="w-1.5 h-1.5 rounded-full bg-orange-500" title={`${statusCounts['in-progress']} in progress`}></div>
        )}
        {statusCounts.completed > 0 && (
          <div className="w-1.5 h-1.5 rounded-full bg-green-500" title={`${statusCounts.completed} completed`}></div>
        )}
      </div>
    );
  };

  const getTileClassName = ({ date, view }) => {
    if (view !== 'month') return '';

    const dateStr = format(date, 'yyyy-MM-dd');
    const dayBookings = bookingsByDate[dateStr] || [];

    if (dayBookings.length > 0) {
      return 'has-bookings';
    }
    return '';
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayBookings = bookingsByDate[dateStr] || [];
    
    if (onDateSelect) {
      onDateSelect(date, dayBookings);
    }
  };

  const getSelectedDateBookings = () => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    return bookingsByDate[dateStr] || [];
  };

  const selectedBookings = getSelectedDateBookings();

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex flex-wrap gap-4 items-center justify-center p-4 bg-zinc-900 rounded-lg border border-zinc-800">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <span className="text-sm text-gray-300">Pending</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-sm text-gray-300">Confirmed</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-orange-500"></div>
          <span className="text-sm text-gray-300">In Progress</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-sm text-gray-300">Completed</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Calendar */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CalendarIcon className="w-5 h-5 text-red-500" />
              <span>Booking Calendar</span>
            </CardTitle>
            <CardDescription>Click on a date to view bookings</CardDescription>
          </CardHeader>
          <CardContent>
            <style>{`
              .react-calendar {
                width: 100%;
                background: transparent;
                border: none;
                font-family: inherit;
                color: white;
              }
              .react-calendar__navigation button {
                color: white;
                min-width: 44px;
                background: none;
                font-size: 16px;
                margin-top: 8px;
              }
              .react-calendar__navigation button:enabled:hover,
              .react-calendar__navigation button:enabled:focus {
                background-color: rgba(239, 68, 68, 0.1);
              }
              .react-calendar__month-view__weekdays {
                text-transform: uppercase;
                font-weight: bold;
                font-size: 0.75rem;
                color: #9ca3af;
              }
              .react-calendar__tile {
                position: relative;
                max-width: 100%;
                padding: 10px 6px;
                background: transparent;
                color: white;
                text-align: center;
                line-height: 16px;
                font-size: 0.875rem;
              }
              .react-calendar__tile:enabled:hover,
              .react-calendar__tile:enabled:focus {
                background-color: rgba(239, 68, 68, 0.1);
              }
              .react-calendar__tile--now {
                background: rgba(239, 68, 68, 0.2);
              }
              .react-calendar__tile--active {
                background: #ef4444 !important;
                color: white !important;
              }
              .react-calendar__tile.has-bookings {
                font-weight: bold;
              }
              .react-calendar__month-view__days__day--neighboringMonth {
                color: #6b7280;
              }
            `}</style>
            <Calendar
              onChange={handleDateChange}
              value={selectedDate}
              tileContent={getTileContent}
              tileClassName={getTileClassName}
            />
          </CardContent>
        </Card>

        {/* Selected Date Bookings */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle>{format(selectedDate, 'MMMM d, yyyy')}</CardTitle>
            <CardDescription>
              {selectedBookings.length} booking{selectedBookings.length !== 1 ? 's' : ''} scheduled
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedBookings.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No bookings on this date</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {selectedBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="border border-zinc-700 rounded-lg p-3 hover:bg-zinc-800 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-red-500" />
                        <span className="font-semibold">{booking.name}</span>
                      </div>
                      <Badge className={
                        booking.status === 'completed' ? 'bg-green-600' :
                        booking.status === 'confirmed' ? 'bg-blue-600' :
                        booking.status === 'in-progress' ? 'bg-orange-600' :
                        booking.status === 'cancelled' ? 'bg-gray-600' :
                        'bg-yellow-600'
                      }>
                        {booking.status}
                      </Badge>
                    </div>
                    <div className="space-y-1 text-sm text-gray-400">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-3 h-3" />
                        <span>{booking.preferred_time || booking.time}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Car className="w-3 h-3" />
                        <span>{booking.service} - {booking.vehicleType}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingCalendar;
